// src/components/Pages/ResellPreparationPage.js

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Box,
    Container,
    Title,
    Text,
    useMantineTheme,
    useMantineColorScheme,
    Paper,
    Loader,
    Group,
    Table,
    Select,
    Checkbox,
    ActionIcon,
    TextInput,
    Textarea,
    Modal,
    Button,
    Tooltip,
    Pagination,
    Radio,
    Center,
    Badge,
    HoverCard, // ADDED
    ScrollArea, // ADDED
    Menu
} from '@mantine/core';
import {
    IconReportAnalytics,
    IconWorld,
    IconLanguage,
    IconRadar2,
    IconTrash,
    IconFileText,
    IconArchive,
    IconRefresh,
    IconArrowBackUp,
    IconSearch,
    IconPencilCheck,
    IconBrain,
    IconSparkles,
    IconInfoCircle,
    IconChevronDown
} from '@tabler/icons-react';
import { useShallow } from 'zustand/shallow';
import { notifications } from '@mantine/notifications';
import copy from 'copy-to-clipboard';
import { getFirestore, collection, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { useBackgroundImage } from '../../context/BackgroundImageContext';
import { useAuth } from '../../context/AuthContext';
import backgroundImageFile from '../../assets/images/brochure-background.jpg';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useResellPipelineStore, { selectPreparationItems } from '../../stores/resellPipelineStore';

// ----------------- Utils -----------------
const CMS_EDIT_URL_BASE = 'https://controlcentre.loveincorporated.com/msnglobal/Content/Gallery/Edit/';

const normalizeDate = (date) => {
    if (!date) return null;
    if (typeof date.toDate === 'function') return date.toDate();
    if (date instanceof Date) return date;
    if (typeof date === 'string') {
        const d = new Date(date);
        return isNaN(d) ? null : d;
    }
    return null;
};

const toISODateString = (date) => {
    const d = normalizeDate(date);
    return d ? d.toLocaleDateString('sv-SE') : null;
};

const toShortDate = (date) => {
    const d = normalizeDate(date);
    if (!d) return '-';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const toShortTime = (date) => {
    const d = normalizeDate(date);
    if (!d) return '';
    // 'en-GB' naturally defaults to a 24-hour format, but hour12: false guarantees it
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
};

const calculateDaysResting = (date) => {
    const lastDate = normalizeDate(date);
    if (!lastDate) return 'NA';
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfLastDate = new Date(lastDate.getFullYear(), lastDate.getMonth(), lastDate.getDate());
    const diff = startOfToday - startOfLastDate;
    return Math.floor(diff / (1000 * 3600 * 24));
};

const calculatePVsPerVar = (pageViews, cdsEntries) => {
    const views = parseInt(pageViews, 10);
    const entries = parseInt(cdsEntries, 10);

    // Fail gracefully if data is missing, malformed, or if entries is 0 (divide by zero)
    if (isNaN(views) || isNaN(entries) || entries === 0) return 'NA';

    const result = Math.round(views / entries);
    return result.toLocaleString('en-US'); // Injects commas for readability (e.g., 1,250)
};

const sanitizeForTSV = (value) => {
    if (value === null || typeof value === 'undefined') return '';
    return String(value).replace(/\t/g, ' ').replace(/(\r\n|\n|\r)/g, ' ');
};

const formatItemsForExcel = (items) => {
    const headers = [
        'Date', 'New Title', 'Canonical ID', 'Type', 'Old Title', 'Send Time', 'New Image Slide', 'Brand', 'Last Entry', 'Last Feed ID', 'PVs per Var'
    ];
    let tsv = headers.join('\t') + '\n';
    items.forEach(item => {
        const row = [
            item.pubDate ? toISODateString(item.pubDate) : '',
            item.newTitle,
            item.originalId,
            item.type,
            item.latestTitle,
            item.pubTime,
            item.newLeadImageSlideNo,
            item.Brand,
            item.lastEntryDate ? toISODateString(item.lastEntryDate) : '',
            item.lastFeedId,
            calculatePVsPerVar(item.pageViews, item.cdsEntries) // <-- NEW
        ];
        tsv += row.map(sanitizeForTSV).join('\t') + '\n';
    });
    return tsv;
};

// --- Audit Logger (Fire & Forget) ---
const logTitleAuditEvent = (eventData) => {
    try {
        const db = getFirestore();
        // Notice we DO NOT 'await' this. We let it execute in the background.
        addDoc(collection(db, 'audit_title_changes'), {
            ...eventData,
            timestamp: serverTimestamp()
        }).catch(err => console.error("Telemetry failed silently:", err));
    } catch (e) {
        console.error("Telemetry construction failed:", e);
    }
};


// ----------------- Row Component -----------------
const PreparationRow = React.memo(function PreparationRow({
    item,
    isDark,
    theme,
    selectedIds,
    usersMap,
    hasManagementRole,
    handleRowSelect,
    handleFieldUpdate,
    onVeto,
    onRequestUpdate,
    onSendToScheduling,
    onArchive,
    onDelete,
    onOpenCommandCenter,
    currentRegion
}) {
    const { currentUser } = useAuth();
    const isSelected = selectedIds.includes(item.id);
    const handleSelect = useCallback(() => handleRowSelect(item.id), [item.id, handleRowSelect]);

    // --- NEW: Focus-Aware Local State ---
    const [localTitle, setLocalTitle] = useState(item.newTitle || '');
    const [isTitleFocused, setIsTitleFocused] = useState(false);

    const [localSlideNo, setLocalSlideNo] = useState(item.newLeadImageSlideNo || '');
    const [isSlideFocused, setIsSlideFocused] = useState(false);



    // --- NEW: The Sync Engine (Global to Local) ---
    // Instantly adopts global state changes (like AI batch updates) ONLY if the user isn't actively typing.
    useEffect(() => {
        if (!isTitleFocused) {
            setLocalTitle(item.newTitle || '');
        }
    }, [item.newTitle, isTitleFocused]);

    useEffect(() => {
        if (!isSlideFocused) {
            setLocalSlideNo(item.newLeadImageSlideNo || '');
        }
    }, [item.newLeadImageSlideNo, isSlideFocused]);

    const handleNewTitleBlur = useCallback(
        (e) => {
            setIsTitleFocused(false); // Release the focus lock
            const newVal = e.currentTarget.value;
            const oldVal = item.newTitle || '';

            // Only update and log if the string actually mutated
            if (newVal !== oldVal) {
                handleFieldUpdate(item.id, 'newTitle', newVal);

                logTitleAuditEvent({
                    userUid: currentUser?.uid || 'unknown',
                    userEmail: currentUser?.email || 'unknown',
                    feedId: item.lastFeedId || item['Last Feed ID'],
                    originalId: item.originalId,
                    brand: item.Brand || 'Unknown',
                    region: item.region || currentRegion || 'Unknown',
                    prior_newTitle: oldVal, // <-- RENAMED: 'oldVal' is already item.newTitle || ''
                    newTitle: newVal,
                    source: 'manual_preparation_table',
                    statusTarget: 'Preparation'
                });
            }
        },
        [item, handleFieldUpdate, currentUser]
    );

    const handleSlideNoBlur = useCallback(
        (e) => {
            setIsSlideFocused(false); // Release the focus lock
            const val = parseInt(e.currentTarget.value, 10);
            handleFieldUpdate(item.id, 'newLeadImageSlideNo', isNaN(val) ? null : val);
        },
        [item.id, handleFieldUpdate]
    );

    return (
        <Table.Tr
            key={item.id}
            style={{
                verticalAlign: 'middle',
                backgroundColor: isSelected ? (isDark ? theme.colors.dark[5] : theme.colors.gray[1]) : undefined
            }}
        >
            <Table.Td>
                <Checkbox checked={isSelected} onChange={handleSelect} />
            </Table.Td>
            <Table.Td>
                <Text size="xs" fw={500}>{item.latestTitle || 'Untitled'}</Text>
            </Table.Td>
            <Table.Td>
                <Textarea
                    placeholder="Enter new title..."
                    value={localTitle}
                    onChange={(e) => setLocalTitle(e.currentTarget.value)}
                    onFocus={() => setIsTitleFocused(true)}
                    onBlur={handleNewTitleBlur}
                    size="xs"
                    autosize
                    minRows={1}
                    maxRows={3}
                />
            </Table.Td>
            <Table.Td>
                <Center>
                    <TextInput
                        placeholder="#"
                        value={localSlideNo}
                        onChange={(e) => setLocalSlideNo(e.currentTarget.value)}
                        onFocus={() => setIsSlideFocused(true)}
                        onBlur={handleSlideNoBlur}
                        size="xs"
                        type="number"
                        styles={{ input: { width: 50, textAlign: 'center' } }}
                    />
                </Center>
            </Table.Td>
            <Table.Td ta="center">
                {item.Brand || 'NA'}
            </Table.Td>
            <Table.Td ta="center">
    <Text size="xs" style={{ lineHeight: 1.2 }}>
        {toShortDate(item.addedAt)}
        <br />
        {toShortTime(item.addedAt)}
    </Text>
</Table.Td>
            <Table.Td ta="center">
                {calculateDaysResting(item.lastEntryDate)}
            </Table.Td>
            <Table.Td ta="center">
                {item.originalId}
            </Table.Td>
            <Table.Td ta="center">
                {item.cdsEntries || 'NA'}
            </Table.Td>
            <Table.Td ta="center">
                <Text size="xs" fw={500}>{calculatePVsPerVar(item.pageViews, item.cdsEntries)}</Text>
            </Table.Td>
            <Table.Td ta="center">
                {item.lastFeedId && (
                    <Tooltip label={`Edit Latest Variant: ${item.lastFeedId}`} withArrow>
                        <Button
                            component="a"
                            href={`${CMS_EDIT_URL_BASE}${item.lastFeedId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="compact-xs"
                            variant="outline"
                            style={{ minWidth: 70, justifyContent: 'center' }}
                        >
                            {item.lastFeedId}
                        </Button>
                    </Tooltip>
                )}
            </Table.Td>
            <Table.Td ta="center">
                <Tooltip label="Send for scheduling" withArrow>
                    <Button
                        size="compact-sm"
                        variant="filled"
                        color="green"
                        onClick={() => onSendToScheduling(item.id)}
                    >
                        Submit
                    </Button>
                </Tooltip>
            </Table.Td>
            <Table.Td>
                <Group justify="center" gap="xs" wrap="nowrap" style={{ align: 'center' }}>
                    <Tooltip label="Block this article (Veto)" withArrow>
                        <Button
                            size="compact-xs"
                            variant="light"
                            color="red"
                            onClick={() => onVeto(item)}
                        >
                            Veto
                        </Button>
                    </Tooltip>
                    <Button.Group>
                        <Tooltip label="Open Multi-Region Command Centre" withArrow>
                            <Button
                                size="compact-xs"
                                variant="light"
                                color="blue"
                                onClick={() => onOpenCommandCenter(item.originalId)}
                                style={{ borderRight: '1px solid rgba(0,0,0,0.2' }}
                            >
                                <IconWorld size={14} style={{ marginRight: 4 }} />
                                Multi Region
                            </Button>
                        </Tooltip>
                        <Tooltip label="Flag ALL variants of this article for update" withArrow>
                            <Button
                                size="compact-xs"
                                variant="light"
                                color="gray"
                                onClick={() => onRequestUpdate(item, 'family')}
                                style={{ borderRight: '1px solid rgba(0,0,0,0.1)' }}
                            >
                                Update Family
                            </Button>
                        </Tooltip>
                        <Menu position="bottom-end" withArrow withinPortal>
                            <Menu.Target>
                                <Tooltip label="More update options" withArrow>
                                    <ActionIcon
                                        size="compact-xs"
                                        variant="light"
                                        color="gray"
                                        style={{ height: '100%' }}
                                    >
                                        <IconChevronDown size={20} />
                                    </ActionIcon>
                                </Tooltip>
                            </Menu.Target>
                            <Menu.Dropdown>
                                <Menu.Item onClick={() => onRequestUpdate(item, 'single')}>
                                    <Text size="xs" c="red">Update This Variant Only</Text>
                                </Menu.Item>
                            </Menu.Dropdown>
                        </Menu>
                    </Button.Group>
                    <Tooltip label="Flag as archived for 7-days" withArrow>
                        <ActionIcon variant="light" color="blue" size="sm" onClick={() => onArchive(item)}>
                            <IconArchive size={14} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Remove this item" withArrow>
                        <ActionIcon variant="subtle" color="red" size="sm" onClick={() => onDelete(item)}>
                            <IconTrash size={14} />
                        </ActionIcon>
                    </Tooltip>

                    {hasManagementRole && (
                        <Text size="xs" c="dimmed" style={{ marginLeft: 4, minWidth: 20, textAlign: 'center' }}>
                            {usersMap[item.addedByUid] || '...'}
                        </Text>
                    )}
                </Group>
            </Table.Td>
        </Table.Tr>
    );
});

// ----------------- Multi Region, Sibling Command Center Modal -----------------
function SiblingManagementModal({ opened, onClose, items, isFetching, onExecute, onTriggerAI, injectedAITitle, isContextGenerating, ccAiTargetId }) {
    const { currentUser } = useAuth();
    const [localEdits, setLocalEdits] = useState({});
    const [selectedSiblingIds, setSelectedSiblingIds] = useState([]);
    const aiSuggestionLedger = useRef({});

    // Hydrate local state and selection when items load
    useEffect(() => {
        const initialEdits = {};
        const initialSelected = [];

        // Define the highly-active English markets for default opt-in
        const PRESELECTED_REGIONS = ['US', 'UK', 'CA', 'AU'];

        items.forEach(item => {
            initialEdits[item.id] = {
                newTitle: item.newTitle || '', // Init blank or with existing draft
                newLeadImageSlideNo: item.newLeadImageSlideNo || ''
            };

            // Opt-in interactive rows ONLY if they are in the core English markets
            const isInteractive = item._priority === 1 || item._priority === 5;
            if (isInteractive && PRESELECTED_REGIONS.includes(item.region)) {
                initialSelected.push(item.id);
            }
        });

        setLocalEdits(initialEdits);
        setSelectedSiblingIds(initialSelected);
    }, [items]);

    // Listen for routed AI Titles
    useEffect(() => {
        if (injectedAITitle) {
            setLocalEdits(prev => ({
                ...prev,
                [injectedAITitle.id]: { ...prev[injectedAITitle.id], newTitle: injectedAITitle.title }
            }));
            // Write to our short-term memory ledger
            aiSuggestionLedger.current[injectedAITitle.id] = injectedAITitle.title;
        }
    }, [injectedAITitle]);

    // Sort Logic: Core Markets Pinned to Top, then Alphanumeric
    const CORE_MARKETS = ['US', 'UK', 'CA', 'AU', 'DE'];
    const sortedItems = useMemo(() => {
        return [...items].sort((a, b) => {
            const aIsCore = CORE_MARKETS.includes(a.region);
            const bIsCore = CORE_MARKETS.includes(b.region);

            if (aIsCore && !bIsCore) return -1;
            if (!aIsCore && bIsCore) return 1;
            if (aIsCore && bIsCore) return CORE_MARKETS.indexOf(a.region) - CORE_MARKETS.indexOf(b.region);

            return `${a.region}_${a.language}`.localeCompare(`${b.region}_${b.language}`);
        });
    }, [items]);



    const handleToggleSelect = (id) => {
        setSelectedSiblingIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleLocalChange = (id, field, value) => {
        setLocalEdits(prev => ({
            ...prev,
            [id]: { ...prev[id], [field]: value }
        }));
    };

    const handlePreFlightAndExecute = () => {
        // Isolate Execution: Only process explicitly selected rows
        const selectedItems = items.filter(i => selectedSiblingIds.includes(i.id));
        const payload = selectedItems.map(item => ({
            ...item,
            newTitle: localEdits[item.id]?.newTitle,
            newLeadImageSlideNo: localEdits[item.id]?.newLeadImageSlideNo
        }));

        // The UI Validation Gate
        const invalidItems = payload.filter(
            item => (item._priority === 1 || item._priority === 5) && !item.newTitle?.trim()
        );

        if (invalidItems.length > 0) {
            notifications.show({
                title: 'Validation Error',
                message: 'All selected variants must have a title before scheduling.',
                color: 'red'
            });
            return;
        }

        // --- NEW: EVALUATE & LOG TELEMETRY ---
        payload.forEach(payloadItem => {
            const finalTitle = payloadItem.newTitle;
            const originalCmsTitle = payloadItem.latestTitle || payloadItem['Latest Editorial Title'] || 'Untitled';
            const aiSuggestion = aiSuggestionLedger.current[payloadItem.id];

            // CRITICAL: Retrieve the untouched original item to capture the true prior draft state
            const originalItem = items.find(i => i.id === payloadItem.id);
            const prior_newTitle = originalItem?.newTitle || '';

            // Only log if they actually provided a new title that differs from the CMS
            if (finalTitle && finalTitle !== originalCmsTitle) {
                let evalSource = 'cc_manual_entry';
                if (aiSuggestion) {
                    // Check for exact match against the ledger
                    evalSource = (finalTitle === aiSuggestion) ? 'cc_ai_context_accepted_as_is' : 'cc_ai_context_edited';
                }

                logTitleAuditEvent({
                    userUid: currentUser?.uid || 'unknown',
                    userEmail: currentUser?.email || 'unknown',
                    feedId: payloadItem.lastFeedId || payloadItem['Last Feed ID'],
                    originalId: payloadItem.originalId || familyOriginalId,
                    brand: payloadItem.Brand || payloadItem.brand || 'Unknown',
                    region: payloadItem.region || 'Unknown',
                    prior_newTitle: prior_newTitle, // <-- RENAMED & strictly blank if no prior draft
                    newTitle: finalTitle,
                    source: evalSource,
                    statusTarget: 'SendToScheduling'
                });
            }
        });

        onExecute(payload);
        onClose();
    };

    // Extract the Canonical ID for the Modal Title
    const familyOriginalId = items.length > 0 ? (items[0].originalId || items[0]['Original ID']) : '';

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={
                <Box>
                    <Title order={3}>Multi-Region Command Centre (CANONICAL: {familyOriginalId ? `${familyOriginalId}` : ''})</Title>

                    <Text size="md" c="gray.6" mt={2} fw={400}>
                        Create new titles and promote regional variants en-masse.
                    </Text>
                </Box>
            }
            size="90%"
            centered
        >
            {isFetching ? (
                <Center p="xl"><Loader /><Text ml="md">Compiling Sibling Variants...</Text></Center>
            ) : (
                <>
                    <Table striped highlightOnHover mb="md" verticalSpacing="sm">
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th w={40}></Table.Th>
                                <Table.Th w={100}>Region & Language</Table.Th>
                                <Table.Th w={50} ta="center">Days Resting</Table.Th>
                                <Table.Th w={200} ta="center">State</Table.Th>
                                <Table.Th w="35%">Article Title (Last Used)</Table.Th>
                                <Table.Th w="35%">New Title</Table.Th>
                                <Table.Th w={60} ta="center">New Image</Table.Th>
                                <Table.Th w={50}></Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {sortedItems.map(item => {
                                const isInteractive = item._priority === 1 || item._priority === 5;
                                const isSelected = selectedSiblingIds.includes(item.id);

                                // Split labels for multiline badge rendering
                                const stateLabels = {
                                    1: { main: 'Upstream', sub: '(Pre-Pipeline)', color: 'orange' },
                                    2: { main: 'Blocked', sub: '(Vetoed)', color: 'red' },
                                    3: { main: 'Update Queue', sub: '(Pending)', color: 'gray' },
                                    4: { main: 'Locked', sub: '(Scheduled)', color: 'teal' },
                                    5: { main: 'Active', sub: '(Preparation)', color: 'blue' }
                                };
                                const stateInfo = stateLabels[item._priority] || { main: 'Unknown', sub: '', color: 'gray' };

                                // Fallbacks for raw Excel header imports
                                const displayTitle = item.latestTitle || item['Latest Editorial Title'] || 'Untitled';
                                const displayLastEntry = item.lastEntryDate || item['Last Entry'];
                                const displayFeedId = item.lastFeedId || item['Last Feed ID'];

                                return (
                                    <Table.Tr
                                        key={item.id}
                                        opacity={isInteractive ? 1 : 0.85}
                                        bg={isSelected ? 'rgba(0,0,0,0.02)' : undefined}
                                    >
                                        <Table.Td>
                                            {isInteractive && (
                                                <Checkbox checked={isSelected} onChange={() => handleToggleSelect(item.id)} />
                                            )}
                                        </Table.Td>
                                        <Table.Td>
                                            <Text fw={600} size="sm" lh={1.1}>{item.region}_{item.language}</Text>
                                            {displayFeedId && (
                                                <Text size="14px" c="dimmed" mt={2}>{displayFeedId}</Text>
                                            )}
                                        </Table.Td>
                                        <Table.Td ta="center">
                                            {calculateDaysResting(displayLastEntry)}
                                        </Table.Td>
                                        <Table.Td ta="center">
                                            <Badge
                                                color={stateInfo.color}
                                                variant="outline"
                                                radius="lg" /* <-- ADDED FORCED RADIUS */
                                                styles={{ root: { height: 'auto', padding: '4px 12px', lineHeight: 1.2 } }}
                                            >
                                                <Text span display="block" size="xs" fw={700}>{stateInfo.main}</Text>
                                                {stateInfo.sub && (
                                                    <Text span display="block" size="10px" fw={500} mt={2}>
                                                        {stateInfo.sub}
                                                    </Text>
                                                )}
                                            </Badge>
                                        </Table.Td>
                                        <Table.Td>
                                            {/* Removed c="dimmed" and bumped font weight for contrast */}
                                            <Text size="sm" fw={500}>{displayTitle}</Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Textarea
                                                value={localEdits[item.id]?.newTitle || ''}
                                                onChange={(e) => handleLocalChange(item.id, 'newTitle', e.currentTarget.value)}
                                                readOnly={!isInteractive || !isSelected} // <-- CHANGED
                                                variant={(!isInteractive || !isSelected) ? 'filled' : 'default'} // <-- ADDED
                                                autosize
                                                minRows={1}
                                                maxRows={3}
                                            />
                                        </Table.Td>
                                        <Table.Td>
                                            <TextInput
                                                value={localEdits[item.id]?.newLeadImageSlideNo || ''}
                                                onChange={(e) => handleLocalChange(item.id, 'newLeadImageSlideNo', e.currentTarget.value)}
                                                readOnly={!isInteractive || !isSelected} // <-- CHANGED
                                                variant={(!isInteractive || !isSelected) ? 'filled' : 'default'} // <-- ADDED
                                                type="number"
                                                //ta="center"
                                                //justify="center"
                                                styles={{ input: { textAlign: 'center' } }} /* <-- TARGETS INNER HTML INPUT */ />
                                        </Table.Td>
                                        <Table.Td>
                                            {isInteractive && (
                                                <Tooltip label="Generate Context-Aware Title" withArrow>
                                                    <ActionIcon
                                                        variant="light"
                                                        color="grape"
                                                        onClick={() => onTriggerAI(item)}
                                                        loading={isContextGenerating && ccAiTargetId === item.id}
                                                        // Disable if row is unselected, OR if another row is currently generating
                                                        disabled={!isSelected || (isContextGenerating && ccAiTargetId !== item.id)}
                                                    >
                                                        <IconSparkles size={16} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            )}
                                        </Table.Td>
                                    </Table.Tr>
                                );
                            })}
                        </Table.Tbody>
                    </Table>
                    <Group justify="flex-end" mt="md">
                        <Button variant="default" onClick={onClose}>Cancel & Discard Changes</Button>
                        <Button color="green" onClick={handlePreFlightAndExecute}>Send to Scheduling ({selectedSiblingIds.length})</Button>
                    </Group>
                </>
            )}
        </Modal>
    );
}

// ----------------- Main Page Component -----------------
function ResellPreparationPage() {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const { isBackgroundImageVisible } = useBackgroundImage();
    const headerTextColor = isBackgroundImageVisible ? theme.white : undefined;

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { userProfile, currentUser } = useAuth();

    // --- Role Check ---
    const hasManagementRole = useMemo(() =>
        userProfile && ['admin', 'management'].includes(userProfile.role),
        [userProfile]
    );

    // --- Store Access ---
    const {
        isLoading, error,
        region, language, brand,
        setRegion, setLanguage, setBrand,
        initializeSubscriptions, cleanup,
        updateField,
        moveToScheduling, vetoItem, requestUpdate, requestFamilyUpdate,
        archiveItem, deleteItem, bulkDelete,
        // --- NEW SIBLING COMMAND CENTER EXPORTS ---
        siblingFamilyItems,
        isFetchingFamily,
        fetchSiblingFamily,
        executeFamilyUpdates
    } = useResellPipelineStore(useShallow(state => ({
        isLoading: state.isLoading,
        error: state.error,
        region: state.region,
        language: state.language,
        brand: state.brand,
        setRegion: state.setRegion,
        setLanguage: state.setLanguage,
        setBrand: state.setBrand,
        initializeSubscriptions: state.initializeSubscriptions,
        cleanup: state.cleanup,
        updateField: state.updateField,
        moveToScheduling: state.moveToScheduling,
        vetoItem: state.vetoItem,
        requestUpdate: state.requestUpdate,
        requestFamilyUpdate: state.requestFamilyUpdate,
        archiveItem: state.archiveItem,
        deleteItem: state.deleteItem,
        bulkDelete: state.bulkDelete,
        // --- NEW SIBLING COMMAND CENTER MAPPINGS ---
        siblingFamilyItems: state.siblingFamilyItems,
        isFetchingFamily: state.isFetchingFamily,
        fetchSiblingFamily: state.fetchSiblingFamily,
        executeFamilyUpdates: state.executeFamilyUpdates
    })));
    const preparationItems = useResellPipelineStore(selectPreparationItems);

    // --- Local State ---
    const [selectedIds, setSelectedIds] = useState([]);
    const [activePage, setPage] = useState(1);
    const ROWS_PER_PAGE = 50;
    const [localUsersMap, setLocalUsersMap] = useState({});

    // --- Sort State ---
    const [sortBy, setSortBy] = useState('addedAt'); // 'addedAt', 'daysResting', 'pvPerVar'
    const [sortDesc, setSortDesc] = useState(true); // true = descending, false = ascending

    // Fetch Users
    useEffect(() => {
        if (currentUser) {
            const db = getFirestore();
            const usersCollection = collection(db, 'users');
            const unsub = onSnapshot(usersCollection, (snapshot) => {
                const map = {};
                snapshot.forEach((d) => {
                    const u = d.data();
                    const firstName = u.firstName || '';
                    const lastName = u.lastName || '';
                    if (firstName && lastName) {
                        map[d.id] = `${firstName[0]}${lastName[0]}`;
                    } else if (firstName) {
                        map[d.id] = `${firstName[0]}`;
                    } else {
                        map[d.id] = '?';
                    }
                });
                setLocalUsersMap(map);
            }, (err) => console.error("User fetch error", err));
            return () => unsub();
        }
    }, [currentUser]);

    // Modals
    const [commandCenterOpen, setCommandCenterOpen] = useState(false);
    const [vetoModalOpen, setVetoModalOpen] = useState(false);
    const [vetoReason, setVetoReason] = useState('');
    const [activeItemForModal, setActiveItemForModal] = useState(null);
    const [updateModalOpen, setUpdateModalOpen] = useState(false);
    const [updateModalType, setUpdateModalType] = useState('family'); // 'single' or 'family'
    const [itemToArchive, setItemToArchive] = useState(null);
    const [itemToDelete, setItemToDelete] = useState(null);

    // AI Titles
    const [isGeneratingTitles, setIsGeneratingTitles] = useState(false);
    const [isContextGenerating, setIsContextGenerating] = useState(false);
    const [isTitleModalOpen, setIsTitleModalOpen] = useState(false);
    const [aiResults, setAiResults] = useState([]);
    const [selectedVariants, setSelectedVariants] = useState({});
    const [generationSource, setGenerationSource] = useState('simple'); // 'simple' | 'context'

    // --- NEW: Multi-Region Command Center AI Routing State ---
    const [ccAiTarget, setCcAiTarget] = useState(null); // Tracks { id: string }
    const [ccAiInjectedTitle, setCcAiInjectedTitle] = useState(null); // Holds the payload returning to the modal

    // --- NEW: Single-Row Command Center AI Trigger ---
    const handleCommandCenterAITrigger = async (item) => {
        // --- CONCURRENCY LOCK ---
        if (isContextGenerating) {
            notifications.show({ title: 'Processing', message: 'Please wait for the current AI generation to finish.', color: 'blue' });
            return;
        }

        // 1. Defensively grab the ID from either the standard schema or the legacy Excel schema
        const targetFeedId = item.lastFeedId || item['Last Feed ID'];

        // 2. Validate against the normalized variable
        if (!currentUser || !targetFeedId) {
            notifications.show({ title: 'Skipped', message: 'Variant lacks a CMS ID to read context.', color: 'orange' });
            return;
        }

        setCcAiTarget({ id: item.id });
        setGenerationSource('context');
        setIsContextGenerating(true);
        setAiResults([]);
        setSelectedVariants({});

        try {
            // 3. Clean the normalized variable
            const cleanCmsId = targetFeedId.toString().split('_')[0];
            const payload = {
                region: region,
                language: language,
                titles: [{
                    id: item.id,
                    // Defensively handle the title as well, just in case!
                    original_title: item.latestTitle || item['Latest Editorial Title'] || 'Untitled',
                    brand: item.Brand || item.brand,
                    cms_id: cleanCmsId
                }]
            };

            const token = await currentUser.getIdToken();
            const res = await fetch(
                'https://us-central1-freelancer-ideas-portal.cloudfunctions.net/generate_context_titles',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify(payload),
                }
            );

            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Failed to generate context titles.');

            const cleanResults = (data.results || []).map(r => ({
                ...r,
                new_variants: r.new_variants.map(v => v.replace(/^"/, '').replace(/"$/, '')),
                debug_context: r.debug_context
            }));

            if (cleanResults.length > 0) {
                setAiResults(cleanResults);
                setIsTitleModalOpen(true);
            } else {
                throw new Error("No results returned.");
            }
        } catch (err) {
            console.error(err);
            notifications.show({ title: 'Context Error', message: err.message, color: 'red' });
        } finally {
            setIsContextGenerating(false);
        }
    };


    // Excel Copy State
    const [isExcelCopied, setIsExcelCopied] = useState(false);

    // --- Search State ---
    const [searchQuery, setSearchQuery] = useState('');

    // --- Lifecycle ---
    useEffect(() => {
        initializeSubscriptions();
        return () => cleanup();
    }, []);

    // Handle URL Params
    useEffect(() => {
        const r = searchParams.get('region');
        const l = searchParams.get('language');
        const b = searchParams.get('brand');
        if (r) setRegion(r);
        if (l) setLanguage(l);
        if (b) setBrand(b);
        if (r || l || b) setSearchParams({}, { replace: true });
    }, [searchParams]);

    // Reset selection/page on filter change OR search
    useEffect(() => {
        setSelectedIds([]);
        setPage(1);
    }, [preparationItems.length, region, language, brand, searchQuery]);

    // --- SEARCH, FILTER & SORT LOGIC ---
    const filteredItems = useMemo(() => {
        let items = preparationItems;

        // 1. Search Filter
        const query = searchQuery.trim().toLowerCase();
        if (query) {
            items = items.filter(item => {
                const originalId = item.originalId ? String(item.originalId).toLowerCase() : '';
                const feedId = item.lastFeedId ? String(item.lastFeedId).toLowerCase() : '';
                return originalId.includes(query) || feedId.includes(query);
            });
        }

        // 2. Dynamic Sort Logic
        return [...items].sort((a, b) => {
            let valA = 0;
            let valB = 0;

            if (sortBy === 'daysResting') {
                // Older dates mean HIGHER days resting. 
                // We use the raw timestamp to avoid UI string formatting issues.
                valA = normalizeDate(a.lastEntryDate)?.getTime() || 0;
                valB = normalizeDate(b.lastEntryDate)?.getTime() || 0;
                // Invert the logic here because older dates (smaller numbers) = more days resting
                if (valA < valB) return sortDesc ? -1 : 1;
                if (valA > valB) return sortDesc ? 1 : -1;
                return 0;
            }
            else if (sortBy === 'pvPerVar') {
                // Calculate raw PV/Var math safely
                valA = (a.cdsEntries > 0) ? (a.pageViews / a.cdsEntries) : 0;
                valB = (b.cdsEntries > 0) ? (b.pageViews / b.cdsEntries) : 0;
            }
            else {
                // Default: addedAt
                valA = normalizeDate(a.addedAt)?.getTime() || 0;
                valB = normalizeDate(b.addedAt)?.getTime() || 0;
            }

            // Standard numerical comparison for PV/Var and addedAt
            if (valA < valB) return sortDesc ? 1 : -1;
            if (valA > valB) return sortDesc ? -1 : 1;
            return 0;
        });

    }, [preparationItems, searchQuery, sortBy, sortDesc]);

    // Calculate pagination based on the FILTERED list
    const totalPages = Math.ceil(filteredItems.length / ROWS_PER_PAGE);
    const paginatedItems = useMemo(() => {
        const start = (activePage - 1) * ROWS_PER_PAGE;
        const end = start + ROWS_PER_PAGE;
        return filteredItems.slice(start, end);
    }, [filteredItems, activePage]);

    const handleRowSelect = useCallback((id) => {
        setSelectedIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
    }, []);

    const handleSelectAll = () => {
        const pageIds = paginatedItems.map(i => i.id);
        const allSelected = pageIds.every(id => selectedIds.includes(id));
        if (allSelected) {
            setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
        } else {
            setSelectedIds(prev => [...new Set([...prev, ...pageIds])]);
        }
    };

    const allOnPageSelected = paginatedItems.length > 0 && paginatedItems.every(i => selectedIds.includes(i.id));
    const someOnPageSelected = paginatedItems.some(i => selectedIds.includes(i.id)) && !allOnPageSelected;

    // --- Actions ---
    const handleSendToScheduling = useCallback((id) => {
        moveToScheduling(id);
    }, [moveToScheduling]);

    const handleBulkSendToScheduling = () => {
        selectedIds.forEach(id => moveToScheduling(id));
        setSelectedIds([]);
        notifications.show({ title: 'Bulk Action', message: 'Selected items sent for scheduling.', color: 'blue' });
    };

    const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
    const handleConfirmBulkDelete = () => {
        bulkDelete(selectedIds);
        setSelectedIds([]); // Clear selection
        setBulkDeleteModalOpen(false);
    };

    const handleOpenCommandCenter = useCallback((originalId) => {
        fetchSiblingFamily(originalId);
        setCommandCenterOpen(true);
    }, [fetchSiblingFamily]);

    const openVetoModal = useCallback((item) => {
        setActiveItemForModal(item);
        setVetoReason('');
        setVetoModalOpen(true);
    }, []);

    const confirmVeto = () => {
        if (!activeItemForModal) return;
        vetoItem(activeItemForModal.id, vetoReason);
        setVetoModalOpen(false);
        setActiveItemForModal(null);
    };

    const openUpdateModal = useCallback((item, type = 'family') => {
        setActiveItemForModal(item);
        setUpdateModalType(type);
        setUpdateModalOpen(true);
    }, []);

    const confirmUpdate = () => {
        if (!activeItemForModal) return;

        if (updateModalType === 'family') {
            requestFamilyUpdate(activeItemForModal.id, activeItemForModal.originalId);
        } else {
            requestUpdate(activeItemForModal.id);
        }

        setUpdateModalOpen(false);
        setActiveItemForModal(null);
    };

    const confirmArchive = () => {
        if (itemToArchive) {
            archiveItem(itemToArchive.id);
            setItemToArchive(null);
        }
    };
    const confirmDelete = () => {
        if (itemToDelete) {
            deleteItem(itemToDelete.id);
            setItemToDelete(null);
        }
    };

    // --- AI Title Handlers ---

    // 1. Standard (Text-Only) Generation
    const handleGenerateTitles = async () => {
        if (selectedIds.length === 0 || !currentUser) return;
        setGenerationSource('simple');
        setIsGeneratingTitles(true);
        setAiResults([]);
        setSelectedVariants({});

        try {
            const selectedItems = preparationItems.filter(item => selectedIds.includes(item.id));
            const payload = {
                region: region,
                language: language,
                titles: selectedItems.map(item => ({
                    id: item.id,
                    original_title: item.latestTitle,
                    brand: item.Brand
                }))
            };
            const token = await currentUser.getIdToken();
            const res = await fetch(
                'https://us-central1-freelancer-ideas-portal.cloudfunctions.net/generateSimpleTitles',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify(payload),
                }
            );
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Failed to generate titles.');
            const cleanResults = (data.results || []).map(r => ({
                ...r,
                new_variants: r.new_variants.map(v => v.replace(/^"/, '').replace(/"$/, ''))
            }));
            if (cleanResults.length > 0) {
                setAiResults(cleanResults);
                setIsTitleModalOpen(true);
            } else {
                throw new Error("No results returned.");
            }
        } catch (err) {
            console.error(err);
            notifications.show({ title: 'Error', message: err.message, color: 'red' });
        } finally {
            setIsGeneratingTitles(false);
        }
    };

    // 2. NEW: Context-Aware Generation (The Challenger)
    const handleGenerateContextTitles = async () => {
        if (selectedIds.length === 0 || !currentUser) return;
        setGenerationSource('context'); // For conditional modal title

        // Filter out items without a CMS ID
        const selectedItems = preparationItems.filter(item => selectedIds.includes(item.id));
        const validItems = selectedItems.filter(item => item.lastFeedId);

        if (validItems.length === 0) {
            notifications.show({
                title: 'Operation Skipped',
                message: 'None of the selected items have a CMS ID (Last Feed ID) required for reading context.',
                color: 'orange'
            });
            return;
        }

        if (validItems.length < selectedItems.length) {
            notifications.show({
                title: 'Partial Selection',
                message: `Skipping ${selectedItems.length - validItems.length} items that lack CMS IDs.`,
                color: 'yellow'
            });
        }

        setIsContextGenerating(true);
        setAiResults([]);
        setSelectedVariants({});
        try {
            const titlesPayload = validItems.map(item => {
                // SANITIZATION: Split "438871_US_EN" -> ["438871", "US", "EN"] -> Take "438871"
                const cleanCmsId = item.lastFeedId.toString().split('_')[0];
                return {
                    id: item.id,
                    original_title: item.latestTitle,
                    brand: item.Brand,
                    cms_id: cleanCmsId
                };
            });

            const payload = {
                region: region,
                language: language,
                titles: titlesPayload
            };

            const token = await currentUser.getIdToken();
            const res = await fetch(
                'https://us-central1-freelancer-ideas-portal.cloudfunctions.net/generate_context_titles',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify(payload),
                }
            );
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Failed to generate context titles.');

            const cleanResults = (data.results || []).map(r => ({
                ...r,
                new_variants: r.new_variants.map(v => v.replace(/^"/, '').replace(/"$/, '')),
                debug_context: r.debug_context // Preserve context for UI
            }));

            if (cleanResults.length > 0) {
                setAiResults(cleanResults);
                setIsTitleModalOpen(true);
            } else {
                throw new Error("No results returned from context service.");
            }
        } catch (err) {
            console.error("Context Generation Failed:", err);
            notifications.show({ title: 'Context Error', message: err.message, color: 'red' });
        } finally {
            setIsContextGenerating(false);
        }
    };

    const handleApplyAiTitles = () => {
        if (ccAiTarget) {
            // ROUTE A: Send payload to the Command Center's local React state
            Object.entries(selectedVariants).forEach(([id, title]) => {
                setCcAiInjectedTitle({ id, title });
            });
            setCcAiTarget(null); // Clear routing lock
        } else {
            // ROUTE B: Standard global Zustand store update
            Object.entries(selectedVariants).forEach(([id, title]) => {
                // targetItem is grabbed BEFORE the state finishes updating, preserving the old draft
                const targetItem = preparationItems.find(i => i.id === id);
                const prior_newTitle = targetItem?.newTitle || '';

                updateField(id, 'newTitle', title);

                // --- NEW: LOG BATCH AI USAGE ---
                if (targetItem && title !== 'Manual Review Required') {
                    logTitleAuditEvent({
                        userUid: currentUser?.uid || 'unknown',
                        userEmail: currentUser?.email || 'unknown',
                        feedId: targetItem.lastFeedId || targetItem['Last Feed ID'],
                        originalId: targetItem.originalId,
                        brand: targetItem.Brand || targetItem.brand || 'Unknown',
                        region: targetItem.region || region || 'Unknown',
                        prior_newTitle: prior_newTitle, // <-- RENAMED & strictly blank if no prior draft
                        newTitle: title,
                        source: generationSource === 'context' ? 'ai_batch_context' : 'ai_batch_simple',
                        statusTarget: 'Preparation'
                    });
                }
            });
            setSelectedIds([]); // Clear main table selections
        }

        setIsTitleModalOpen(false);
        setAiResults([]);
        setSelectedVariants({});
        notifications.show({ title: 'Updated', message: 'Titles applied successfully.', color: 'green' });
    };

    const handleCopyToExcel = () => {
        const tsvString = formatItemsForExcel(preparationItems);
        try {
            copy(tsvString, { format: 'text/plain' });
            setIsExcelCopied(true);
            setTimeout(() => setIsExcelCopied(false), 2000);
            notifications.show({
                title: 'Copied to Clipboard',
                message: `${preparationItems.length} rows copied. You can now paste into Excel.`,
                color: 'green',
            });
        } catch (err) {
            notifications.show({
                title: 'Copy Failed',
                message: 'Could not copy data to clipboard. See console for error.',
                color: 'red',
            });
            console.error("Failed to copy for Excel", err);
        }
    };

    // --- Render Content ---
    return (
        <Box style={{
            width: '100%', flexGrow: 1, display: 'flex', flexDirection: 'column',
            backgroundImage: isBackgroundImageVisible ? `url(${backgroundImageFile})` : 'none',
            backgroundColor: !isBackgroundImageVisible ? (isDark ? theme.colors.dark[7] : theme.colors.gray[0]) : 'transparent',
            backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'scroll',
        }}>
            {/* --- SECTION 1: HEADER & FILTERS --- */}
            <Container size="xl" py="xl">
                <Title order={1} ta="center" mb="xs" style={{ color: headerTextColor }}>
                    <IconPencilCheck size={32} color={theme.colors.yellow[isDark || isBackgroundImageVisible ? 4 : 6]} style={{ marginRight: theme.spacing.sm, verticalAlign: 'middle' }} />
                    Resell Preparation
                </Title>
                <Text size="lg" ta="center" mb="xl" style={{ color: headerTextColor }}>
                    Refine titles and select images for resell candidates.
                </Text>

                <Paper withBorder shadow="sm" p="md" radius="md" mb="md">
                    <Group grow>
                        <Select
                            label="Region"
                            leftSection={<IconWorld size={16} />}
                            data={['UK', 'US', 'AE', 'AU', 'CA', 'DE', 'ES', 'FR', 'ZA']}
                            value={region}
                            onChange={(v) => setRegion(v || 'US')}
                        />
                        <Select
                            label="Language"
                            leftSection={<IconLanguage size={16} />}
                            data={['EN', 'DE', 'ES', 'FR']}
                            value={language}
                            onChange={(v) => setLanguage(v || 'EN')}
                        />
                        <Select
                            label="Brand"
                            leftSection={<IconRadar2 size={16} />}
                            data={['All', 'loveMONEY', 'loveFOOD', 'loveEXPLORING', 'lovePROPERTY']}
                            value={brand}
                            onChange={(v) => setBrand(v || 'All')}
                        />
                    </Group>

                    {/* --- SEARCH INPUT --- */}
                    <TextInput
                        placeholder="Search by Original ID or Last Feed ID..."
                        leftSection={<IconSearch size={16} />}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.currentTarget.value)}
                        mt="md"
                        rightSection={
                            searchQuery && (
                                <ActionIcon size="xs" radius="xl" color="gray" variant="subtle" onClick={() => setSearchQuery('')}>
                                    <IconTrash size={12} />
                                </ActionIcon>
                            )
                        }
                    />

                    {/* Management Actions */}
                    {hasManagementRole && (
                        <Group justify="flex-end" mt="md">
                            <Button
                                variant="outline"
                                color={isExcelCopied ? 'teal' : 'green'}
                                onClick={handleCopyToExcel}
                                leftSection={isExcelCopied ? <IconRefresh size={16} /> : <IconFileText size={16} />}
                                size="xs"
                            >
                                {isExcelCopied ? 'Copied!' : 'Copy All for Excel'}
                            </Button>
                            <Button
                                variant="outline"
                                size="xs"
                                onClick={() => navigate(`/resell-radar?region=${region}&language=${language}&brand=${brand}`)}
                            >
                                Go to Resell Radar
                            </Button>
                        </Group>
                    )}
                </Paper>
            </Container>

            {/* --- SECTION 2: BULK ACTIONS --- */}
            {selectedIds.length > 0 && (
                <Container size="xl" pb="md">
                    <Paper withBorder shadow="sm" p="sm" radius="md" mb="md" style={{ borderColor: theme.colors.blue[5] }}>
                        <Group justify="space-between">
                            <Text size="sm" fw={500} style={{ marginLeft: 12 }} >{selectedIds.length} item(s) selected</Text>
                            <Group>
                                {/* AI Standard */}
                                <Tooltip label="Auto-headline generator. Uses legacy article title." withArrow>
                                    <Button
                                        size="sm"
                                        color="blue"
                                        variant="outline"
                                        onClick={handleGenerateTitles}
                                        loading={isGeneratingTitles}
                                        leftSection={<IconBrain size={16} />}
                                        style={{ marginLeft: 12 }}
                                    >
                                        Simple Headline Generator
                                    </Button>
                                </Tooltip>

                                {/* NEW: Context-Aware AI */}
                                <Tooltip label="[New, Beta:] Takes time to read the article intro for improved accuracy." withArrow>
                                    <Button
                                        size="sm"
                                        color="grape" // Distinction color
                                        variant="filled"
                                        onClick={handleGenerateContextTitles}
                                        loading={isContextGenerating}
                                        leftSection={<IconSparkles size={16} />}
                                        style={{ marginRight: 12 }}
                                    >
                                        Context-Aware Headlines
                                    </Button>
                                </Tooltip>

                                {/* Bulk Delete */}

                                <Button
                                    size="sm"
                                    color="red"
                                    variant="outline"
                                    onClick={() => setBulkDeleteModalOpen(true)}
                                    leftSection={<IconTrash size={16} />}

                                >
                                    Remove Selected
                                </Button>

                                <Button
                                    size="sm"
                                    color="green"
                                    onClick={handleBulkSendToScheduling}
                                    style={{ marginRight: 12 }}
                                >
                                    Submit (for Scheduling)
                                </Button>
                            </Group>
                        </Group>
                    </Paper>
                </Container>
            )}

            {/* --- SECTION 3: MAIN TABLE --- */}
            <Container fluid px="xl" pb="xl">
                <Paper withBorder shadow="sm" radius="md" p="md">
                    {isLoading ? (
                        <Center p="xl"><Loader /><Text ml="md">Loading...</Text></Center>
                    ) : paginatedItems.length === 0 ? (
                        <Center p="xl"><Text c="dimmed">No preparation items found.</Text></Center>
                    ) : (
                        <>
                            <Table.ScrollContainer minWidth={700}>
                                <Table striped highlightOnHover verticalSpacing="sm">
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th w={40}>
                                                <Checkbox
                                                    checked={allOnPageSelected}
                                                    indeterminate={someOnPageSelected}
                                                    onChange={handleSelectAll}
                                                />
                                            </Table.Th>
                                            <Table.Th w={300}>Article Title (Last Used)</Table.Th>
                                            <Table.Th w={300}>New Title</Table.Th>
                                            <Tooltip label="Enter the slide number that contains the new lead image." withArrow>
                                                <Table.Th style={{ width: 90, textAlign: 'center' }}>
                                                    New<br /> Image
                                                </Table.Th>
                                            </Tooltip>
                                            <Table.Th w={100} ta="center">Brand</Table.Th>
                                            <Table.Th w={80} ta="center" style={{ cursor: 'pointer' }} onClick={() => {
                                                if (sortBy === 'addedAt') {
                                                    setSortDesc(!sortDesc);
                                                } else {
                                                    setSortBy('addedAt');
                                                    setSortDesc(true);
                                                }
                                            }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.2 }}>
                                                    <span>Added</span>
                                                    <span>{sortBy === 'addedAt' ? (sortDesc ? '▼' : '▲') : '='}</span>
                                                </div>
                                            </Table.Th>
                                            <Table.Th w={80} ta="center" style={{ cursor: 'pointer' }} onClick={() => {
                                                if (sortBy === 'daysResting') {
                                                    setSortDesc(!sortDesc); // Toggle direction if already sorting by this
                                                } else {
                                                    setSortBy('daysResting');
                                                    setSortDesc(true); // Default to descending when newly selected
                                                }
                                            }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.2 }}>
                                                    <span>Days Resting</span>
                                                    <span>{sortBy === 'daysResting' ? (sortDesc ? '▼' : '▲') : '='}</span>
                                                </div>
                                            </Table.Th>
                                            <Table.Th w={80} ta="center">Original ID</Table.Th>
                                            <Table.Th w={80} ta="center">Variants</Table.Th>
                                            <Table.Th w={90} ta="center" style={{ cursor: 'pointer' }} onClick={() => {
                                                if (sortBy === 'pvPerVar') {
                                                    setSortDesc(!sortDesc); // Toggle direction if already selected
                                                } else {
                                                    setSortBy('pvPerVar');
                                                    setSortDesc(true); // Default to Highest PVs first when newly selected
                                                }
                                            }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.2 }}>
                                                    <span>PVs/Var</span>
                                                    <span>{sortBy === 'pvPerVar' ? (sortDesc ? '▼' : '▲') : '='}</span>
                                                </div>
                                            </Table.Th>
                                            <Table.Th w={120} ta="center">Last Variant</Table.Th>
                                            <Table.Th w={120} ta="center">Ready</Table.Th>
                                            <Table.Th w={100} ta="center">- Other Actions -</Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {paginatedItems.map(item => (
                                            <PreparationRow
                                                key={item.id}
                                                item={item}
                                                isDark={isDark}
                                                theme={theme}
                                                selectedIds={selectedIds}
                                                usersMap={localUsersMap}
                                                hasManagementRole={hasManagementRole}
                                                handleRowSelect={handleRowSelect}
                                                handleFieldUpdate={updateField}
                                                onVeto={openVetoModal}
                                                onRequestUpdate={openUpdateModal}
                                                onSendToScheduling={handleSendToScheduling}
                                                onArchive={setItemToArchive}
                                                onDelete={setItemToDelete}
                                                onOpenCommandCenter={handleOpenCommandCenter}
                                                currentRegion={region}
                                            />
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </Table.ScrollContainer>
                            {totalPages > 1 && (
                                <Group justify="center" mt="xl">
                                    <Pagination total={totalPages} value={activePage} onChange={setPage} />
                                </Group>
                            )}
                        </>
                    )}
                </Paper>
            </Container>

            {/* --- MODALS --- */}

            {/* Multi Region/Sibling Command Center Modal */}
            <SiblingManagementModal
                opened={commandCenterOpen}
                onClose={() => {
                    setCommandCenterOpen(false);
                    setCcAiTarget(null); // Safety reset
                }}
                items={siblingFamilyItems}
                isFetching={isFetchingFamily}
                onExecute={executeFamilyUpdates}
                onTriggerAI={handleCommandCenterAITrigger}
                injectedAITitle={ccAiInjectedTitle}
                isContextGenerating={isContextGenerating}
                ccAiTargetId={ccAiTarget?.id}
            />

            {/* Veto Modal */}
            <Modal opened={vetoModalOpen} onClose={() => setVetoModalOpen(false)} title="Block / Veto Article" centered>
                <Text size="sm" mb="md">Why is this article unsuitable for resell?</Text>
                <Textarea
                    placeholder="e.g., Superseded by newer article, Content outdated..."
                    minRows={3}
                    value={vetoReason}
                    onChange={(e) => setVetoReason(e.currentTarget.value)}
                />
                <Group justify="flex-end" mt="md">
                    <Button variant="default" onClick={() => setVetoModalOpen(false)}>Cancel</Button>
                    <Button color="red" onClick={confirmVeto} disabled={!vetoReason.trim()}>Confirm Veto</Button>
                </Group>
            </Modal>

            {/* Update Request Modal */}
            <Modal opened={updateModalOpen} onClose={() => setUpdateModalOpen(false)} title="Request Major Update" centered>
                <Text size="sm">
                    {updateModalType === 'family' ? (
                        <>Are you sure you want to mark <b>ALL VARIANTS</b> of <br /><i>{activeItemForModal?.latestTitle}</i> for a major update?</>
                    ) : (
                        <>Are you sure you want to mark ONLY <b>this variant</b> <br />(<i>{activeItemForModal?.latestTitle}</i>) for a major update?</>
                    )}
                </Text>

                {updateModalType === 'family' && activeItemForModal?.originalId && (
                    <Text size="xs" fw={500} mt="xs" c="blue">
                        Canonical ID: {activeItemForModal.originalId}
                    </Text>
                )}

                <Text size="xs" c="dimmed" mt="xs">
                    {updateModalType === 'family'
                        ? "This will remove all sibling articles from the Radar and Pipeline, flagging them en-masse for the editorial team to rewrite. IMPORTANT: Existing siblings in the Update Queue will be reinserted (anew)."
                        : "This will remove only this specific variant from the pipeline. Other variants will remain."}
                </Text>
                <Group justify="flex-end" mt="md">
                    <Button variant="default" onClick={() => setUpdateModalOpen(false)}>Cancel</Button>
                    <Button color="orange" onClick={confirmUpdate}>Confirm Request</Button>
                </Group>
            </Modal>

            {/* Archive Confirmation */}
            <Modal opened={!!itemToArchive} onClose={() => setItemToArchive(null)} title="Confirm 7-Day Archive Flag" centered size="sm">
                <Text size="sm">Are you sure you want to archive this article? (Removes from pipeline and flags for seven days.)</Text>
                <Group justify="flex-end" mt="md">
                    <Button variant="default" onClick={() => setItemToArchive(null)}>Cancel</Button>
                    <Button color="blue" onClick={confirmArchive}>Archive Item</Button>
                </Group>
            </Modal>

            {/* Delete Confirmation */}
            <Modal opened={!!itemToDelete} onClose={() => setItemToDelete(null)} title="Confirm Removal" centered size="sm">
                <Text size="sm">Are you sure you want to delete this article from the pipeline?</Text>
                <Group justify="flex-end" mt="md">
                    <Button variant="default" onClick={() => setItemToDelete(null)}>Cancel</Button>
                    <Button color="red" onClick={confirmDelete}>Delete Item</Button>
                </Group>
            </Modal>

            {/* Bulk Delete Confirmation Modal */}
            <Modal opened={bulkDeleteModalOpen} onClose={() => setBulkDeleteModalOpen(false)} title="Confirm Bulk Deletion" centered>
                <Text size="sm" mb="md" c="red" fw={500}>
                    Are you sure you want to DELETE {selectedIds.length} items?
                </Text>
                <Text size="sm" mb="md">
                    This action is permanent and cannot be undone. These items will be removed from the pipeline.
                </Text>
                <Group justify="flex-end" mt="md">
                    <Button variant="default" onClick={() => setBulkDeleteModalOpen(false)}>Cancel</Button>
                    <Button color="red" onClick={handleConfirmBulkDelete}>Delete All Selected</Button>
                </Group>
            </Modal>

            {/* AI Title Modal */}
            <Modal
                opened={isTitleModalOpen}
                onClose={() => setIsTitleModalOpen(false)}
                title={
                    <Group>
                        <Text fw={700}>Select Suggested Titles</Text>
                        {generationSource === 'context' ? (
                            <Badge color="grape" variant="filled">Context-Aware</Badge>
                        ) : (
                            <Badge color="blue" variant="filled">Simple Headline</Badge>
                        )}
                    </Group>
                }
                centered
                size="xl"
                scrollAreaComponent={Paper}
            >
                <Text size="sm" c="dimmed" mb="md" style={{ fontStyle: 'italic' }}>
                    {generationSource === 'context'
                        ? "These suggestions were generated by analysing the article's full introduction for deeper context."
                        : "These suggestions were generated based on the original headline only."}
                </Text>

                <Box style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '1rem' }}>
                    {aiResults.map((result) => (
                        <Paper withBorder p="md" mb="md" key={result.id}>
                            <Group position="apart" mb="sm">
                                <Text size="sm" c="dimmed" style={{ flex: 1 }}>
                                    Original: {result.original_title}
                                </Text>
                                {/* EXPLAINABILITY LAYER */}
                                {result.debug_context && (
                                    <HoverCard width={400} shadow="md">
                                        <HoverCard.Target>
                                            <ActionIcon variant="light" color="grape" size="sm">
                                                <IconInfoCircle size={16} />
                                            </ActionIcon>
                                        </HoverCard.Target>
                                        <HoverCard.Dropdown>
                                            <Text size="xs" fw={700} mb="xs" c="grape">Source Context Used (Intro):</Text>
                                            <ScrollArea h={240} type="auto">
                                                <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>
                                                    {result.debug_context}
                                                </Text>
                                            </ScrollArea>
                                        </HoverCard.Dropdown>
                                    </HoverCard>
                                )}
                            </Group>

                            <Radio.Group
                                value={selectedVariants[result.id] || null}
                                onChange={(val) => setSelectedVariants(c => ({ ...c, [result.id]: val }))}
                            >
                                <Group mt="xs" display="block">
                                    {result.new_variants.map((v, i) => (
                                        <Radio key={i} value={v} label={v} mb="xs" />
                                    ))}
                                    <Radio
                                        value="Manual Review Required"
                                        label={<Text c="orange" fw={500} size="sm">Manual Review Required</Text>}
                                    />
                                </Group>
                            </Radio.Group>
                        </Paper>
                    ))}
                </Box>
                <Group justify="flex-end" mt="xl">
                    <Button variant="default" onClick={() => setIsTitleModalOpen(false)}>Cancel</Button>
                    <Button
                        color={generationSource === 'context' ? "grape" : "green"}
                        onClick={handleApplyAiTitles}
                    >
                        Apply Selected
                    </Button>
                </Group>
            </Modal>
        </Box>
    );
}

export default ResellPreparationPage;